import { Link, useParams } from "react-router-dom";
import { FiExternalLink } from "react-icons/fi";
import { Badge, Spinner } from "@/components/ui";
import { SECTION_LABELS } from "@/config/constants";
import { getStrapiMedia } from "@/lib/strapi";
import { ROUTES } from "@/routes/paths";
import { useDesigner } from "../hooks/useDesigner";

export function DesignerDetailPage() {
  const { documentId } = useParams();
  const { data: designer, isLoading, error } = useDesigner(documentId, {
    populate: { logo: true, products: { populate: "images" } },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (error || !designer) {
    return <p className="text-destructive">Designer not found.</p>;
  }

  const logo = getStrapiMedia(designer.logo);
  const products = designer.products ?? [];

  return (
    <div className="space-y-8">
      <div className="rounded-lg border border-border bg-card p-6">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary">
            {logo ? (
              <img src={logo} alt={designer.name} className="h-full w-full object-cover" />
            ) : (
              <span className="text-2xl font-medium text-muted-foreground">
                {designer.name?.[0] ?? "?"}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-foreground">{designer.name}</h1>
            {designer.speciality && (
              <p className="text-primary">{designer.speciality}</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {designer.country && <span>{designer.country}</span>}
              {designer.section && (
                <Badge variant="primary">
                  {SECTION_LABELS[designer.section] ?? designer.section}
                </Badge>
              )}
            </div>
            {designer.website_url && (
              <a
                href={designer.website_url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                Visit website <FiExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </div>

      <div>
        <h2 className="mb-4 text-xl font-semibold text-foreground">
          Products by {designer.name}
        </h2>
        {products.length === 0 ? (
          <p className="text-muted-foreground">No products listed yet.</p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => {
              const img = getStrapiMedia(p.images?.[0]);
              return (
                <Link
                  key={p.documentId}
                  to={ROUTES.PRODUCT_DETAIL(p.documentId)}
                  className="overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary"
                >
                  <div className="aspect-square bg-secondary">
                    {img && (
                      <img src={img} alt={p.title} className="h-full w-full object-cover" />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="truncate font-medium text-foreground">{p.title}</p>
                    <p className="text-sm text-accent">${p.price}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DesignerDetailPage;
